import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Calendar, MapPin, IndianRupee, Plus, Search, Loader2, Medal } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SPORTS, STATUS_STYLES, bannerUrl, formatDate } from '../../components/tournament-hub/tournamentHubUtils';
import { API } from '../../utils/apiConfig';

function TournamentCard({ t, onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-left bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-200 group"
      data-testid={`tournament-card-${t.id}`}
    >
      <div className="h-36 relative overflow-hidden">
        {t.banner_file_id ? (
          <img src={bannerUrl(t.banner_file_id)} alt={t.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 flex items-center justify-center">
            <Trophy className="w-10 h-10 text-white/80" />
          </div>
        )}
        <span className={`absolute top-2 right-2 text-[10px] font-bold uppercase px-2 py-1 rounded-full ${STATUS_STYLES[t.status] || 'bg-slate-100 text-slate-700'}`}>
          {t.status}
        </span>
      </div>
      <div className="p-4 space-y-1.5">
        <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide">{t.sport} · {t.tournament_type}</p>
        <h3 className="font-bold text-slate-900 leading-snug line-clamp-2">{t.name}</h3>
        <div className="flex items-center gap-1 text-xs text-slate-500"><Calendar className="w-3.5 h-3.5" />{formatDate(t.tournament_date)}</div>
        {t.venue && <div className="flex items-center gap-1 text-xs text-slate-500"><MapPin className="w-3.5 h-3.5" />{t.venue}</div>}
        {!!t.prize_pool && <div className="flex items-center gap-1 text-xs font-semibold text-emerald-600"><IndianRupee className="w-3.5 h-3.5" />{Number(t.prize_pool).toLocaleString('en-IN')}</div>}
      </div>
    </button>
  );
}

export default function TournamentHubPage() {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sport, setSport] = useState('all');

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (sport !== 'all') params.sport = sport;
      const res = await axios.get(`${API}/tournament-hub`, { params });
      setTournaments(res.data.tournaments || []);
    } catch (e) {
      // silent - public page
    } finally {
      setLoading(false);
    }
  }, [search, sport]);

  useEffect(() => {
    const timer = setTimeout(fetchList, 300);
    return () => clearTimeout(timer);
  }, [fetchList]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50" data-testid="tournament-hub-page">
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="flex items-center gap-2 text-amber-100 text-sm font-semibold uppercase tracking-widest mb-2">
            <Medal className="w-5 h-5" /> Tournaments Hub
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Discover tournaments from organisers everywhere</h1>
          <p className="text-amber-100 max-w-xl mb-6">A shared directory of pickleball, badminton and multi-sport tournaments — organised by clubs, academies and communities.</p>
          <Button onClick={() => navigate('/hub/submit')} className="bg-white text-orange-600 hover:bg-amber-50 font-semibold" data-testid="submit-tournament-cta">
            <Plus className="w-4 h-4 mr-2" /> List Your Tournament
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search tournaments..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="hub-search-input" />
          </div>
          <Select value={sport} onValueChange={setSport}>
            <SelectTrigger className="w-44" data-testid="hub-sport-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sports</SelectItem>
              {SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
        ) : tournaments.length === 0 ? (
          <div className="text-center py-16 text-slate-500" data-testid="hub-empty-state">
            <Trophy className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            No tournaments found. Be the first to list one!
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {tournaments.map((t) => (
              <TournamentCard key={t.id} t={t} onClick={() => navigate(`/hub/${t.id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
